import { useState, useMemo, useCallback } from "react";
import { useAtom } from "jotai";
import { toast } from "react-toastify";
import { questionsConfigAtom } from "../formConfigAuto";
import { buildFormStateFromLeadData } from "../leadAutoDataFormatter";
import { updateLeadAuto } from "../../../../../services/graal-api/leadAutoService";
import useLeadAutoItem from "../useLeadAutoItem";

const IA_API_URL = process.env.REACT_APP_IA_ASSISTANT_URL || "http://localhost:3005";
const IA_API_KEY = process.env.REACT_APP_IA_API_KEY || "";

/**
 * Champs requis pour l'estimation de prime (identique au web)
 */
const REQUIRED_FIELDS = [
  { key: "marque_vehicule", label: "Marque du véhicule" },
  { key: "modele_vehicule", label: "Modèle du véhicule" },
  { key: "puissance_fiscale", label: "Puissance fiscale" },
  { key: "date_premiere_circulation", label: "Date de 1ère circulation" },
  { key: "conducteur1_date_naissance", label: "Date de naissance" },
  { key: "conducteur1_date_permis", label: "Date du permis" },
  { key: "conducteur1_bonus_malus", label: "Bonus/Malus" },
  { key: "usage_vehicule", label: "Usage du véhicule" },
  { key: "type_garage", label: "Type de garage" },
  { key: "formule", label: "Formule d'assurance" },
];

/**
 * Hook pour gérer l'estimation de prime depuis le backoffice.
 * Appelle la même API IA que le formulaire web et sauvegarde le résultat dans le lead.
 */
const useEstimatePremium = (leadData) => {
  const [questionsConfig] = useAtom(questionsConfigAtom);
  const { actions: leadActions } = useLeadAutoItem();

  const [isEstimating, setIsEstimating] = useState(false);
  const [estimation, setEstimation] = useState(null);
  const [estimationError, setEstimationError] = useState(null);

  // Construire le formState à partir des données actuelles du lead
  const formState = useMemo(() => {
    return buildFormStateFromLeadData(leadData, questionsConfig);
  }, [leadData, questionsConfig]);

  // Vérifier les champs manquants
  const missingFields = useMemo(() => {
    if (!formState) return REQUIRED_FIELDS;

    return REQUIRED_FIELDS.filter(({ key }) => {
      const value = formState[key];
      return value === undefined || value === null || value === "";
    });
  }, [formState]);

  const canEstimate = missingFields.length === 0;

  /**
   * Lance l'estimation de prime via l'API IA, puis sauvegarde le résultat dans le lead
   */
  const estimatePremium = useCallback(async () => {
    if (!canEstimate) return;

    setIsEstimating(true);
    setEstimationError(null);
    setEstimation(null);

    try {
      const response = await fetch(`${IA_API_URL}/api/estimate-premium`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": IA_API_KEY,
        },
        body: JSON.stringify({ formData: formState }),
      });

      const result = await response.json();

      if (result.success) {
        setEstimation(result.estimation);

        // Sauvegarder la prime dans le lead (même logique que le web)
        const updateData = { ...formState };
        if (result.estimation.prime_annuelle_ttc) {
          updateData.prime_annuelle_ttc = result.estimation.prime_annuelle_ttc;
        }
        if (result.estimation.fourchette_min) {
          updateData.fourchette_prime_annuelle_min =
            result.estimation.fourchette_min;
        }
        if (result.estimation.fourchette_max) {
          updateData.fourchette_prime_annuelle_max =
            result.estimation.fourchette_max;
        }

        const payload = {
          ...updateData,
          type_produit: "AUTO",
          id: leadData.id,
          id_utilisateur: leadData.user?.id || null,
          source_lead: leadData.productdata?.source_lead ?? null,
        };

        await updateLeadAuto(payload);
        toast.success("Estimation de prime enregistrée avec succès");

        // Rafraîchir le lead pour afficher les nouvelles données
        await leadActions.refreshLead();
      } else {
        setEstimationError(result.error || "Erreur lors de l'estimation");
        toast.error(result.error || "Erreur lors de l'estimation");
      }
    } catch (err) {
      console.error("Erreur estimation prime:", err);
      setEstimationError("Impossible de contacter le serveur d'estimation");
      toast.error("Impossible de contacter le serveur d'estimation");
    } finally {
      setIsEstimating(false);
    }
  }, [canEstimate, formState, leadData, leadActions]);

  return {
    isEstimating,
    estimation,
    estimationError,
    canEstimate,
    missingFields,
    estimatePremium,
  };
};

export default useEstimatePremium;
